import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BellRing, CheckCircle2, CreditCard, Loader2, Lock, Percent, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useLaunchpadNotify } from "@/hooks/useLaunchpad";
import { cn } from "@/lib/utils";

/* ═══════════════ Notify me ═══════════════ */

export function CardsNotifyMe({
  source,
  className,
  label = "Join the waitlist",
}: {
  source: string;
  className?: string;
  label?: string;
}) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const notify = useLaunchpadNotify();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(value)) {
      toast.error("Enter a valid email address");
      return;
    }
    try {
      await notify.mutateAsync({ email: value, source });
      setDone(true);
      toast.success("You're on the list — we'll email you when the ACEX Card opens.");
    } catch {
      toast.error("Could not add you to the list. Please try again.");
    }
  };

  if (done) {
    return (
      <div className={cn("flex items-center gap-2 text-sm text-primary", className)}>
        <CheckCircle2 className="h-4 w-4" />
        <span>You're on the list — we'll notify you at launch.</span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={cn("flex w-full max-w-md flex-col gap-2 sm:flex-row", className)}>
      <Input
        type="email"
        inputMode="email"
        autoComplete="email"
        aria-label="Email address"
        placeholder="you@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-11 rounded-full"
      />
      <Button type="submit" className="h-11 shrink-0 gap-2 rounded-full px-6" disabled={notify.isPending}>
        {notify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
        {label}
      </Button>
    </form>
  );
}

/* ═══════════════ Locked action ═══════════════ */

export function CardsLockedAction({ label = "Get card", full }: { label?: string; full?: boolean }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn("inline-flex", full && "w-full")}>
            <Button variant="secondary" disabled className={cn("gap-2 rounded-full", full && "w-full")}>
              <Lock className="h-3.5 w-3.5" />
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>Launching soon</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* ═══════════════ In-app coming soon surface ═══════════════ */

const AT_LAUNCH = [
  { icon: ShieldCheck, title: "Verify once", desc: "A single ID and address check unlocks card issuing on your account." },
  { icon: CreditCard, title: "Instant virtual card", desc: "Your card details are issued immediately and work with Apple Pay and Google Pay." },
  { icon: Percent, title: "Cashback on spend", desc: "Cashback is paid straight back into your wallet on every purchase." },
];

export interface CardsComingSoonProps {
  /** Where the signup came from — used to segment the notify list. */
  source?: string;
  className?: string;
}

export default function CardsComingSoon({ source = "cards", className }: CardsComingSoonProps) {
  return (
    <div className={cn("space-y-6", className)}>
      <section className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-background p-6 sm:p-10">
        <div className="flex max-w-2xl flex-col gap-5">
          <Badge variant="secondary" className="w-fit gap-1.5">
            <CreditCard className="h-3.5 w-3.5" /> ACEX Card
          </Badge>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">The ACEX Card is coming soon</h1>
          <p className="text-sm text-muted-foreground sm:text-base">
            Card issuing, top-ups and spending are locked while we finish testing. Add your email and we'll tell you
            the moment applications open.
          </p>
          <CardsNotifyMe source={source} />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            <span>No cards can be issued, funded or charged until the product is live.</span>
          </div>
        </div>
      </section>

      <div className="grid gap-3 sm:grid-cols-3">
        {AT_LAUNCH.map((s, i) => (
          <Card key={s.title}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <s.icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">
                    <span className="text-muted-foreground">{i + 1}. </span>
                    {s.title}
                  </p>
                  <p className="text-xs text-muted-foreground">At launch</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">{s.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="flex flex-col gap-2 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Wallet className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold">Get your balance ready</p>
              <p className="text-xs text-muted-foreground">
                Card top-ups will draw from your fiat wallet balance at launch.
              </p>
            </div>
          </div>
          <Button asChild variant="outline" className="rounded-full">
            <a href="/app/wallets">View wallets</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
